import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Users, Briefcase, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { getToken } from '../../services/authService';

interface StudentMetrics {
  user_id: number;
  name: string;
  email: string;
  weeks: {
    week_date: string;
    seguidores: number | null;
    impresiones: number | null;
    visualizaciones: number | null;
    busquedas: number | null;
    contactos_reclutador: number | null;
    entrevistas: number | null;
    ofertas: number | null;
    ssi_total: number | null;
    ssi_marca: number | null;
    ssi_personas: number | null;
    ssi_interactuar: number | null;
    ssi_relaciones: number | null;
    [key: string]: any;
  }[];
  postulaciones: {
    empresa: string;
    puesto: string;
    fecha: string;
    origen: string;
    fase: number;
  }[];
}

const API_BASE = process.env.REACT_APP_AUTH_API
  || (process.env.NODE_ENV === 'production' ? '/api/linkedin' : 'http://localhost:8080/api/linkedin');

function linkedinUrl(path: string) {
  const base = API_BASE.replace(/\/auth$/, '/linkedin').replace(/\/linkedin$/, '/linkedin');
  return base.endsWith('/linkedin') ? `${base}/${path}` : `${API_BASE}/${path}`;
}

function fmt(d: string) {
  const [, m, dd] = d.split('-');
  return `${dd}/${m}`;
}

export default function AdminLinkedIn() {
  const [students, setStudents] = useState<StudentMetrics[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [view, setView] = useState<'individual' | 'agregado'>('individual');

  useEffect(() => {
    async function load() {
      const token = getToken();
      if (!token) return;
      try {
        const res = await fetch(linkedinUrl('admin-metrics.php'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setStudents(data.students ?? []);
        }
      } catch { /* silent */ }
      setLoading(false);
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const totalStudents = students.length;
  const totalPostulaciones = students.reduce((s, st) => s + st.postulaciones.length, 0);
  const totalOfertas = students.reduce((s, st) => s + st.postulaciones.filter(p => p.fase === 3).length, 0);

  return (
    <div className="p-6 md:p-8">
      <div className="max-w-6xl">
        <div className="flex items-center justify-between mb-1">
          <h1 className="text-2xl font-bold text-gray-800">LinkedIn — Alumnos</h1>
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setView('individual')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                view === 'individual' ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Individual
            </button>
            <button
              onClick={() => setView('agregado')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                view === 'agregado' ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Agregado
            </button>
          </div>
        </div>
        <p className="text-gray-500 text-sm mb-6">Evolución de métricas y postulaciones de todos los alumnos.</p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={<Users size={20} />} value={String(totalStudents)} label="Alumnos trackeando" color="bg-purple-100 text-purple-600" />
          <StatCard icon={<TrendingUp size={20} />} value={bestGrowth(students)} label="Mayor crecimiento seg." color="bg-green-100 text-green-600" />
          <StatCard icon={<Briefcase size={20} />} value={String(totalPostulaciones)} label="Total postulaciones" color="bg-blue-100 text-blue-600" />
          <StatCard icon={<Briefcase size={20} />} value={String(totalOfertas)} label="Ofertas conseguidas" color="bg-emerald-100 text-emerald-600" />
        </div>

        {view === 'individual' ? (
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left p-3 font-semibold text-gray-600">Alumno</th>
                  <th className="text-right p-3 font-semibold text-gray-600">Seguidores</th>
                  <th className="text-right p-3 font-semibold text-gray-600">Impresiones</th>
                  <th className="text-right p-3 font-semibold text-gray-600">SSI</th>
                  <th className="text-right p-3 font-semibold text-gray-600">Postulaciones</th>
                  <th className="text-right p-3 font-semibold text-gray-600">Ofertas</th>
                  <th className="text-center p-3 font-semibold text-gray-600">Mediciones</th>
                  <th className="text-center p-3 font-semibold text-gray-600"></th>
                </tr>
              </thead>
              <tbody>
                {students.map(st => {
                  const lastWeek = st.weeks[st.weeks.length - 1];
                  const isExpanded = expanded === st.user_id;
                  const offers = st.postulaciones.filter(p => p.fase === 3).length;

                  return (
                    <React.Fragment key={st.user_id}>
                      <tr
                        className="border-b hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => setExpanded(isExpanded ? null : st.user_id)}
                      >
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            {isExpanded ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
                            <div>
                              <div className="font-medium text-gray-800">{st.name}</div>
                              <div className="text-xs text-gray-400">{st.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="text-right p-3 font-medium">{lastWeek?.seguidores?.toLocaleString('es') ?? '—'}</td>
                        <td className="text-right p-3">{lastWeek?.impresiones?.toLocaleString('es') ?? '—'}</td>
                        <td className="text-right p-3">{lastWeek?.ssi_total != null ? `${lastWeek.ssi_total}/100` : '—'}</td>
                        <td className="text-right p-3">{st.postulaciones.length || '—'}</td>
                        <td className="text-right p-3">
                          <span className={offers > 0 ? 'text-green-600 font-semibold' : 'text-gray-400'}>{offers}</span>
                        </td>
                        <td className="text-center p-3">
                          <span className="inline-block px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                            {st.weeks.length}
                          </span>
                        </td>
                        <td className="text-center p-3">
                          <Link
                            to={`/admin/linkedin/${st.user_id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded transition-colors"
                            onClick={e => e.stopPropagation()}
                          >
                            <ExternalLink size={12} /> Ver
                          </Link>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={8} className="p-0">
                            <ExpandedDetail student={st} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <AggregatedView students={students.filter(s => s.email !== 'lucianorayos@gmail.com')} />
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, value, label, color }: { icon: React.ReactNode; value: string; label: string; color: string }) {
  return (
    <div className="bg-white border rounded-xl p-4">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${color}`}>{icon}</div>
      <div className="text-xl font-bold text-gray-800">{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}

function ExpandedDetail({ student }: { student: StudentMetrics }) {
  const faseLabels = ['Aplicado', 'Respuesta', 'Entrevista', 'Oferta'];
  const faseColors = ['text-red-500', 'text-yellow-600', 'text-blue-600', 'text-green-600'];

  return (
    <div className="bg-gray-50 p-4 border-t">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Evolución */}
        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Evolución semanal</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr>
                  <th className="text-left p-1 text-gray-500">Fecha</th>
                  <th className="text-right p-1 text-gray-500">Seg.</th>
                  <th className="text-right p-1 text-gray-500">Imp.</th>
                  <th className="text-right p-1 text-gray-500">Vis.</th>
                  <th className="text-right p-1 text-gray-500">Búsq.</th>
                  <th className="text-right p-1 text-gray-500">Reclu.</th>
                  <th className="text-right p-1 text-gray-500">Entr.</th>
                  <th className="text-right p-1 text-gray-500">SSI</th>
                  <th className="text-left p-1 text-gray-500">Posicion.</th>
                </tr>
              </thead>
              <tbody>
                {student.weeks.map((w, i) => {
                  const prevSSI = i > 0 ? student.weeks[i - 1].ssi_total : null;
                  const ssiDelta = w.ssi_total != null && prevSSI != null ? w.ssi_total - prevSSI : null;
                  const pos = (w as any).posicionamiento as string | null;
                  const prevPos = i > 0 ? (student.weeks[i - 1] as any).posicionamiento as string | null : null;
                  const posChanged = i > 0 && pos && prevPos && pos !== prevPos;
                  return (
                    <tr key={w.week_date} className="border-t border-gray-200">
                      <td className="p-1 text-gray-600">{fmt(w.week_date)}</td>
                      <td className="text-right p-1">{w.seguidores?.toLocaleString('es') ?? '—'}</td>
                      <td className="text-right p-1">{w.impresiones?.toLocaleString('es') ?? '—'}</td>
                      <td className="text-right p-1">{w.visualizaciones ?? '—'}</td>
                      <td className="text-right p-1">{w.busquedas ?? '—'}</td>
                      <td className="text-right p-1">{w.contactos_reclutador ?? '—'}</td>
                      <td className="text-right p-1">{w.entrevistas ?? '—'}</td>
                      <td className="text-right p-1">
                        {w.ssi_total != null ? (
                          <span>
                            {w.ssi_total}
                            {ssiDelta != null && (
                              <span className={`ml-1 text-[10px] ${ssiDelta > 0 ? 'text-green-600' : ssiDelta < 0 ? 'text-red-500' : 'text-gray-400'}`}>
                                {ssiDelta > 0 ? '+' : ''}{ssiDelta}
                              </span>
                            )}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="p-1">
                        {pos ? (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${posChanged ? 'bg-yellow-100 text-yellow-700 font-medium' : 'bg-gray-100 text-gray-600'}`}>
                            {pos}
                          </span>
                        ) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Postulaciones */}
        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">
            Postulaciones ({student.postulaciones.length})
          </h4>
          {student.postulaciones.length === 0 ? (
            <p className="text-xs text-gray-400">Sin postulaciones registradas.</p>
          ) : (
            <div className="overflow-x-auto max-h-48 overflow-y-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr>
                    <th className="text-left p-1 text-gray-500">Empresa</th>
                    <th className="text-left p-1 text-gray-500">Puesto</th>
                    <th className="text-center p-1 text-gray-500">Origen</th>
                    <th className="text-center p-1 text-gray-500">Fase</th>
                  </tr>
                </thead>
                <tbody>
                  {student.postulaciones.map((p, i) => (
                    <tr key={i} className="border-t border-gray-200">
                      <td className="p-1 text-gray-700">{p.empresa}</td>
                      <td className="p-1 text-gray-600">{p.puesto}</td>
                      <td className="p-1 text-center">
                        <span className={`text-[10px] font-medium ${p.origen === 'inbound' ? 'text-green-600' : 'text-blue-600'}`}>
                          {p.origen === 'inbound' ? 'Inbound' : 'Outbound'}
                        </span>
                      </td>
                      <td className="p-1 text-center">
                        <span className={`text-[10px] font-semibold ${faseColors[p.fase]}`}>{faseLabels[p.fase]}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AggregatedView({ students }: { students: StudentMetrics[] }) {
  const metricKeys = ['seguidores', 'impresiones', 'visualizaciones', 'busquedas', 'contactos_reclutador', 'entrevistas', 'ofertas', 'ssi_total'];
  const cumulativeKeys = new Set(['contactos_reclutador', 'entrevistas', 'ofertas']);

  function median(arr: number[]): number {
    if (arr.length === 0) return 0;
    const sorted = [...arr].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  }

  const maxWeeks = Math.max(...students.map(s => s.weeks.length), 0);

  const aggregated = Array.from({ length: maxWeeks }, (_, weekIdx) => {
    const row: Record<string, any> = { label: `S${weekIdx + 1}` };
    metricKeys.forEach(key => {
      const values: number[] = [];
      students.forEach(st => {
        if (weekIdx < st.weeks.length) {
          const val = (st.weeks[weekIdx] as any)[key];
          if (val != null) values.push(val);
        }
      });
      if (values.length === 0) { row[key] = null; row[`${key}_med`] = null; return; }
      if (cumulativeKeys.has(key)) {
        row[key] = values.reduce((a, b) => a + b, 0);
        row[`${key}_med`] = null;
      } else {
        row[key] = values.reduce((a, b) => a + b, 0) / values.length;
        row[`${key}_med`] = median(values);
      }
      row[`${key}_n`] = values.length;
    });
    return row;
  });

  return (
    <div className="space-y-6">
      {/* Summary row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {(() => {
          const last = aggregated[aggregated.length - 1];
          const first = aggregated[0];
          if (!last || !first) return null;
          const segGrowth = first.seguidores && last.seguidores
            ? (((last.seguidores - first.seguidores) / first.seguidores) * 100).toFixed(0)
            : null;
          const totalContactos = students.reduce((s, st) => s + st.weeks.reduce((a, w) => a + (w.contactos_reclutador || 0), 0), 0);
          const totalEntrevistas = students.reduce((s, st) => s + st.weeks.reduce((a, w) => a + (w.entrevistas || 0), 0), 0);
          const avgSSI = last.ssi_total ? last.ssi_total.toFixed(1) : '—';
          return (
            <>
              <StatCard icon={<TrendingUp size={20} />} value={segGrowth ? `+${segGrowth}%` : '—'} label="Crec. seguidores (S1→última)" color="bg-green-100 text-green-600" />
              <StatCard icon={<Users size={20} />} value={String(totalContactos)} label="Contactos reclutador (total)" color="bg-blue-100 text-blue-600" />
              <StatCard icon={<Briefcase size={20} />} value={String(totalEntrevistas)} label="Entrevistas (total)" color="bg-purple-100 text-purple-600" />
              <StatCard icon={<TrendingUp size={20} />} value={avgSSI} label="SSI promedio (última S)" color="bg-yellow-100 text-yellow-600" />
            </>
          );
        })()}
      </div>

      {/* Tabla agregada por semana relativa */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="p-4 border-b bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-700">Evolución por semana relativa</h3>
          <p className="text-xs text-gray-400 mt-0.5">S1 = primera medición de cada alumno, S2 = segunda, etc. Promedio / Mediana para perfil, totales para resultados. N = alumnos con datos.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-3 font-semibold text-gray-600">Semana</th>
                <th className="text-right p-3 font-semibold text-gray-600">Seg.</th>
                <th className="text-right p-3 font-semibold text-gray-600">Imp.</th>
                <th className="text-right p-3 font-semibold text-gray-600">Vis.</th>
                <th className="text-right p-3 font-semibold text-gray-600">Búsq.</th>
                <th className="text-right p-3 font-semibold text-gray-600">Reclu. (Σ)</th>
                <th className="text-right p-3 font-semibold text-gray-600">Entr. (Σ)</th>
                <th className="text-right p-3 font-semibold text-gray-600">Ofertas (Σ)</th>
                <th className="text-right p-3 font-semibold text-gray-600">SSI</th>
                <th className="text-center p-3 font-semibold text-gray-600">N</th>
              </tr>
            </thead>
            <tbody>
              {aggregated.map(row => (
                <tr key={row.label} className="border-b hover:bg-gray-50">
                  <td className="p-3 text-gray-800 font-medium whitespace-nowrap">{row.label}</td>
                  <td className="text-right p-3">
                    {row.seguidores != null ? <><span>{Math.round(row.seguidores).toLocaleString('es')}</span><span className="block text-[10px] text-gray-400">med: {Math.round(row.seguidores_med).toLocaleString('es')}</span></> : '—'}
                  </td>
                  <td className="text-right p-3">
                    {row.impresiones != null ? <><span>{Math.round(row.impresiones).toLocaleString('es')}</span><span className="block text-[10px] text-gray-400">med: {Math.round(row.impresiones_med).toLocaleString('es')}</span></> : '—'}
                  </td>
                  <td className="text-right p-3">
                    {row.visualizaciones != null ? <><span>{Math.round(row.visualizaciones).toLocaleString('es')}</span><span className="block text-[10px] text-gray-400">med: {Math.round(row.visualizaciones_med).toLocaleString('es')}</span></> : '—'}
                  </td>
                  <td className="text-right p-3">
                    {row.busquedas != null ? <><span>{Math.round(row.busquedas).toLocaleString('es')}</span><span className="block text-[10px] text-gray-400">med: {Math.round(row.busquedas_med).toLocaleString('es')}</span></> : '—'}
                  </td>
                  <td className="text-right p-3 font-medium">{row.contactos_reclutador != null ? row.contactos_reclutador : '—'}</td>
                  <td className="text-right p-3 font-medium">{row.entrevistas != null ? row.entrevistas : '—'}</td>
                  <td className="text-right p-3 font-medium">{row.ofertas != null ? row.ofertas : '—'}</td>
                  <td className="text-right p-3">
                    {row.ssi_total != null ? <><span>{row.ssi_total.toFixed(1)}</span><span className="block text-[10px] text-gray-400">med: {row.ssi_total_med.toFixed(1)}</span></> : '—'}
                  </td>
                  <td className="text-center p-3 text-xs text-gray-400">{row.seguidores_n ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Variación porcentual semana a semana */}
      {aggregated.length >= 2 && (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-700">Variación entre semanas (%)</h3>
            <p className="text-xs text-gray-400 mt-0.5">Cambio porcentual de una semana relativa a la siguiente.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left p-3 font-semibold text-gray-600">Semana</th>
                  {['Seg.', 'Imp.', 'Vis.', 'Búsq.', 'SSI'].map(h => (
                    <th key={h} className="text-right p-3 font-semibold text-gray-600">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {aggregated.slice(1).map((row, i) => {
                  const prev = aggregated[i];
                  const pct = (key: string) => {
                    if (row[key] == null || prev[key] == null || prev[key] === 0) return '—';
                    const d = ((row[key] - prev[key]) / prev[key] * 100).toFixed(1);
                    const num = parseFloat(d);
                    return <span className={num > 0 ? 'text-green-600' : num < 0 ? 'text-red-500' : 'text-gray-400'}>{num > 0 ? '+' : ''}{d}%</span>;
                  };
                  return (
                    <tr key={row.label} className="border-b hover:bg-gray-50">
                      <td className="p-3 text-gray-800 font-medium">{row.label}</td>
                      <td className="text-right p-3">{pct('seguidores')}</td>
                      <td className="text-right p-3">{pct('impresiones')}</td>
                      <td className="text-right p-3">{pct('visualizaciones')}</td>
                      <td className="text-right p-3">{pct('busquedas')}</td>
                      <td className="text-right p-3">{pct('ssi_total')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function bestGrowth(students: StudentMetrics[]): string {
  let best = 0;
  let bestName = '—';
  students.forEach(st => {
    if (st.weeks.length < 2) return;
    const first = st.weeks.find(w => w.seguidores != null)?.seguidores;
    const last = [...st.weeks].reverse().find(w => w.seguidores != null)?.seguidores;
    if (first && last && first > 0) {
      const growth = ((last - first) / first) * 100;
      if (growth > best) { best = growth; bestName = st.name.split(' ')[0]; }
    }
  });
  return best > 0 ? `${bestName} +${best.toFixed(0)}%` : '—';
}
